/**
 * Profile holdings for the Positions tab, scoped to the viewer.
 */

import {
    useInfiniteQuery,
    useQuery,
    type InfiniteData,
    type UseInfiniteQueryResult,
    type UseQueryResult,
} from '@tanstack/react-query';

import { createApiClient } from 'lib/api/createApiClient';
import { useAuthStore } from 'lib/stores/authStore';
import type { SocialProfileHoldingsVo } from 'models';
import type { GetSocialProfileHoldingsRequest } from 'models/reqres';
import config from 'utils/config';

import { useSocialProfile } from './socialProfile';
import { parseVisibilityState, type SurfaceVisibility } from './socialProfileVisibility';
import type { SocialViewerScope } from './socialViewerScope';
import { removeEmpty } from './utils';

const api = createApiClient({ baseURL: `${config.exchangeApiUrl}/v1` });

const DEFAULT_PAGE_SIZE = 20;
const AVAILABILITY_PROBE_LIMIT = 1;
const STALE_TIME = 30 * 1000;
const AVAILABILITY_STALE_TIME = 60 * 1000;
const GC_TIME = 5 * 60 * 1000;

export type SocialProfileHoldingsFilter = 'open' | 'closed';

/** Viewer scope plus id for authed endpoints. */
export interface SocialProfileSurfaceScope {
    scope: SocialViewerScope;
    viewerUserId?: string;
}

export interface SocialProfileHoldingsPage {
    holdings: SocialProfileHoldingsVo[];
    cursor: string | undefined;
    visibilityState: SurfaceVisibility;
}

/**
 * Resolve the profile surface route; missing viewer id means public.
 */
export function resolveSocialProfileSurfaceScope({
    nickname,
    ownNickname,
    isLoggedIn,
    viewerUserId,
}: {
    nickname: string;
    ownNickname: string | undefined;
    isLoggedIn: boolean;
    viewerUserId: string | null | undefined;
}): SocialProfileSurfaceScope {
    if (!isLoggedIn || !viewerUserId) {
        return { scope: 'public' };
    }
    if (!!ownNickname && nickname === ownNickname) {
        return { scope: 'own', viewerUserId };
    }
    return { scope: 'profile_view', viewerUserId };
}

export interface UseSocialProfileSurfaceScopeResult {
    scope: SocialProfileSurfaceScope;
    /** False while own nickname resolves. */
    ready: boolean;
}

/**
 * Resolve viewer scope from auth plus the cached own profile.
 */
export function useSocialProfileSurfaceScope(nickname: string): UseSocialProfileSurfaceScopeResult {
    const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
    const viewerUserId = useAuthStore((s) => s.userId);
    const ownProfileQuery = useSocialProfile({ enabled: isLoggedIn });

    const ownNickname = ownProfileQuery.data?.social_profile?.nickname;
    const scope = resolveSocialProfileSurfaceScope({ nickname, ownNickname, isLoggedIn, viewerUserId });
    const ready = !isLoggedIn || !viewerUserId || ownProfileQuery.isSuccess || ownProfileQuery.isError;

    return { scope, ready };
}

export const socialProfileHoldingsKeys = {
    all: ['socialProfileHoldings'] as const,
    list: (nickname: string, filter: SocialProfileHoldingsFilter, scope: SocialProfileSurfaceScope) =>
        [
            ...socialProfileHoldingsKeys.all,
            'list',
            filter,
            removeEmpty({ nickname, scope: scope.scope, viewerUserId: scope.viewerUserId }),
        ] as const,
    closedAvailability: (nickname: string, scope: SocialProfileSurfaceScope) =>
        [
            ...socialProfileHoldingsKeys.all,
            'closedAvailability',
            removeEmpty({ nickname, scope: scope.scope, viewerUserId: scope.viewerUserId }),
        ] as const,
};

function buildSocialProfileHoldingsRequest(
    nickname: string,
    scope: SocialProfileSurfaceScope,
    params: { limit: number; closedPositions: boolean; cursor?: string },
): { url: string; queryParams: Record<string, unknown> } {
    const shared = {
        limit: params.limit,
        closed_positions: params.closedPositions,
        cursor: params.cursor,
    };
    if (scope.scope === 'own' && scope.viewerUserId) {
        return { url: `users/${scope.viewerUserId}/social/profile/holdings`, queryParams: removeEmpty(shared) };
    }
    if (scope.scope === 'profile_view' && scope.viewerUserId) {
        return {
            url: `users/${scope.viewerUserId}/social/profile_view/holdings`,
            queryParams: removeEmpty({ ...shared, nickname }),
        };
    }
    return { url: 'social/profile/holdings', queryParams: removeEmpty({ ...shared, nickname }) };
}

async function fetchSocialProfileHoldingsPage(
    nickname: string,
    filter: SocialProfileHoldingsFilter,
    scope: SocialProfileSurfaceScope,
    cursor: string | undefined,
    signal?: AbortSignal,
): Promise<SocialProfileHoldingsPage> {
    const { url, queryParams } = buildSocialProfileHoldingsRequest(nickname, scope, {
        limit: DEFAULT_PAGE_SIZE,
        closedPositions: filter === 'closed',
        cursor,
    });
    const response = await api.get<GetSocialProfileHoldingsRequest>(url, { params: queryParams, signal });
    return {
        holdings: normalizeSocialProfileHoldings(response.data.holdings),
        cursor: response.data.cursor,
        visibilityState: parseVisibilityState(response.data.visibility_state),
    };
}

/**
 * Cursor-paginated holdings keyed by profile, filter, and viewer scope.
 */
export function useSocialProfileHoldingsInfinite(
    nickname: string,
    filter: SocialProfileHoldingsFilter,
    options?: { enabled?: boolean },
): UseInfiniteQueryResult<InfiniteData<SocialProfileHoldingsPage, string | undefined>, Error> {
    const { scope, ready } = useSocialProfileSurfaceScope(nickname);
    return useInfiniteQuery({
        queryKey: socialProfileHoldingsKeys.list(nickname, filter, scope),
        queryFn: ({ pageParam, signal }) => fetchSocialProfileHoldingsPage(nickname, filter, scope, pageParam, signal),
        initialPageParam: undefined as string | undefined,
        // Falsy cursor means end-of-list.
        getNextPageParam: (lastPage) => lastPage.cursor || undefined,
        staleTime: STALE_TIME,
        gcTime: GC_TIME,
        retry: 2,
        enabled: !!nickname && ready && (options?.enabled ?? true),
    });
}

/**
 * Limit-1 probe for whether the Closed filter should be shown.
 */
export function useSocialProfileClosedHoldingsAvailability(
    nickname: string,
    options?: { enabled?: boolean },
): UseQueryResult<boolean, Error> {
    const { scope, ready } = useSocialProfileSurfaceScope(nickname);
    return useQuery({
        queryKey: socialProfileHoldingsKeys.closedAvailability(nickname, scope),
        queryFn: async ({ signal }) => {
            const { url, queryParams } = buildSocialProfileHoldingsRequest(nickname, scope, {
                limit: AVAILABILITY_PROBE_LIMIT,
                closedPositions: true,
            });
            const response = await api.get<GetSocialProfileHoldingsRequest>(url, { params: queryParams, signal });
            return response.data.holdings.length > 0 || !!response.data.cursor;
        },
        staleTime: AVAILABILITY_STALE_TIME,
        gcTime: GC_TIME,
        retry: 2,
        enabled: !!nickname && ready && (options?.enabled ?? true),
    });
}

/**
 * Normalize degraded holdings that arrive without event-level grouping.
 */
export function normalizeSocialProfileHoldings(holdings: SocialProfileHoldingsVo[]): SocialProfileHoldingsVo[] {
    const hasEmptyEventTickers = holdings.some((vo) => !vo.event_ticker && vo.market_holdings?.length > 0);
    if (!hasEmptyEventTickers) {
        return holdings;
    }

    const normalized: SocialProfileHoldingsVo[] = [];
    holdings.forEach((vo) => {
        if (vo.market_holdings && vo.market_holdings.length > 0) {
            normalized.push({
                event_ticker: '',
                series_ticker: vo.series_ticker || '',
                market_holdings: vo.market_holdings,
                total_absolute_position:
                    vo.total_absolute_position ||
                    vo.market_holdings.reduce((sum, m) => sum + Math.abs(m.signed_open_position), 0),
            });
        } else if (vo.event_ticker) {
            normalized.push(vo);
        }
    });
    return normalized;
}

/**
 * Flatten pages, deduping by `event_ticker|series_ticker`.
 */
export function flattenSocialProfileHoldingsPages(
    pages: SocialProfileHoldingsPage[] | undefined,
): SocialProfileHoldingsVo[] {
    if (!pages?.length) {
        return [];
    }
    const seen = new Set<string>();
    const holdings: SocialProfileHoldingsVo[] = [];
    pages.forEach((page) => {
        page.holdings.forEach((holding) => {
            const key = `${holding.event_ticker}|${holding.series_ticker}`;
            if (seen.has(key)) {
                return;
            }
            seen.add(key);
            holdings.push(holding);
        });
    });
    return holdings;
}

/**
 * Surface visibility from the latest page; empty/unknown fails open.
 */
export function resolveSocialProfileHoldingsVisibility(
    pages: SocialProfileHoldingsPage[] | undefined,
): SurfaceVisibility {
    if (!pages?.length) {
        return 'visible';
    }
    return pages[pages.length - 1].visibilityState;
}
